import { isEqual } from 'utils/objects';
import { isEqual as isEqualArray } from 'utils/arrays';

/**
 * Determines if a specific field in a data object has been modified compared to its original value
 *
 * @param {Object} params - The parameters object
 * @param {Object} params.data - The current data object containing the field to check
 * @param {Object} params.originalData - The original data object to compare against
 * @param {string} params.field - The field name to check for changes
 * @returns {boolean} - Returns true if the field has been modified, false otherwise
 *
 * @example
 * // Returns true if the name field has changed
 * const modified = isTouched({
 *   data: { name: 'New Name' },
 *   originalData: { name: 'Old Name' },
 *   field: 'name'
 * });
 */
export default ({ data, originalData, field }) => {
    // Validate inputs to prevent errors
    if (!data || typeof data !== 'object') return false;
    if (!originalData || typeof originalData !== 'object') return false;
    if (!field || typeof field !== 'string') return false;

    // Safely access values
    const originalValue = Object.prototype.hasOwnProperty.call(originalData, field)
        ? originalData[field]
        : undefined;
    const newValue = Object.prototype.hasOwnProperty.call(data, field) ? data[field] : undefined;

    // If both values are undefined or null, they're considered equal (not touched)
    if (
        (originalValue === undefined && newValue === undefined) ||
        (originalValue === null && newValue === null)
    ) {
        return false;
    }

    let isTouched;

    // Handle arrays
    if (Array.isArray(originalValue) || Array.isArray(newValue)) {
        // If one is array and other isn't, they're definitely different
        if (!Array.isArray(originalValue) || !Array.isArray(newValue)) {
            return true;
        }
        isTouched = !isEqualArray(originalValue, newValue);
    }
    // Handle objects (excluding null)
    else if (
        (typeof originalValue === 'object' && originalValue !== null) ||
        (typeof newValue === 'object' && newValue !== null)
    ) {
        // If one is object and other isn't, they're definitely different
        if (
            typeof originalValue !== 'object' ||
            originalValue === null ||
            typeof newValue !== 'object' ||
            newValue === null
        ) {
            return true;
        }
        isTouched = !isEqual(originalValue, newValue);
    }
    // Handle primitives and other cases
    else {
        isTouched = originalValue !== newValue;
    }

    return isTouched;
};
